// faqData.js
const faqData = [
  {
    id: 'sec-a',
    icon: '🏠',
    title: 'General & Platform Overview',
    items: [
      {
        id: 'A1',
        q: 'What is Appraisal Hunter?',
        a: `Appraisal Hunter is a B2B SaaS platform for real estate <strong>desktop appraisal management</strong>. It automates the end-to-end workflow of receiving orders, retrieving comparable property data, reviewing comparables, generating final PDF appraisal reports, and managing billing via a credit system. Powered by <strong>TrueFocus Automation</strong>.`,
      },
      {
        id: 'A2',
        q: 'Who is Appraisal Hunter designed for?',
        a: `<ul>
          <li>Mortgage lenders and clients who submit appraisal orders</li>
          <li>Appraisers who process and complete desktop appraisals</li>
          <li>Appraisal management companies (Admins)</li>
          <li>Private investors and real estate brokers/agents</li>
        </ul>`,
      },
      {
        id: 'A3',
        q: 'What is a Desktop Appraisal?',
        a: `A Desktop Appraisal is a property valuation completed <strong>remotely without a physical site visit</strong>, using data, comparable property sales, and analytical tools. Appraisal Hunter automates data retrieval and report generation steps.`,
      },
      {
        id: 'A4',
        q: 'What are the main features of the platform?',
        a: `<ul>
          <li>Order placement (single and bulk upload)</li>
          <li>Real-time order queue management</li>
          <li>Comparable property data review and selection</li>
          <li>Identification & Market Area analysis (TPAR/TMAR)</li>
          <li>Relative Comparative Analysis</li>
          <li>Final PDF appraisal report generation</li>
          <li>Credit-based billing</li>
          <li>Multi-organization (multi-tenant) support</li>
          <li>Role-based access control (3 user roles)</li>
          <li>Payment history & transaction tracking</li>
          <li>Daily, custom range, and finance reporting</li>
        </ul>`,
      },
      {
        id: 'A5',
        q: 'What product types does Appraisal Hunter support?',
        a: `Appraisal Hunter offers three Desktop Appraisal product tiers:
        <ul>
          <li><strong>Desktop Appraisal-Basic</strong> — Automated desktop valuation from property data and comps. Best for quick screening.</li>
          <li><strong>Desktop Appraisal-Reviewed</strong> — Basic valuation, checked by a licensed appraiser before delivery.</li>
          <li><strong>Desktop Appraisal-Certified</strong> — Full desktop appraisal, signed by a licensed appraiser for lending use.</li>
        </ul>
        Each tier has its own price per credit — check the <strong>My Settings</strong> page for your organization's pricing.`,
      },
      {
        id: 'A7',
        q: 'Is Appraisal Hunter a multi-tenant platform?',
        a: `Yes. The platform supports multiple organizations, each with its own users, order queue, and wallet/credits. Your data is never visible to other organizations.`,
      },
      {
        id: 'A8',
        q: 'How do I contact support?',
        a: `Email: <a href="mailto:ahsupport@truefocusnow.com">ahsupport@truefocusnow.com</a><br/><br/>For account access issues, contact your Organization Administrator first. For billing issues, include your Payment ID in the request.`,
      },
    ],
  },
  {
    id: 'sec-b',
    icon: '📝',
    title: 'Registration & Account Setup',
    items: [
      {
        id: 'B1',
        q: 'How do I create an account?',
        a: `Visit <a href="https://appraisal.truefocusautomation.com/register">appraisal.truefocusautomation.com/register</a> and fill in the self-signup form with: First Name, Last Name, Organization Name, Business Address (Line 1, Line 2, City, State, Zip), Email, Password, and optionally Phone Number. Accept the Terms & Conditions and submit.`,
      },
      {
        id: 'B2',
        q: 'What happens after I register?',
        a: `Your organization account is <strong>auto-approved</strong>. You can log in immediately. A default credit wallet is created for your organization. Next, add credits using your credit or debit card from the Payments page so you can start placing orders.`,
      },
      {
        id: 'B3',
        q: 'I got a message saying my organization already exists. What do I do?',
        a: `A company account under that name has already been created.
        <ul>
          <li>Contact your <strong>Organization Administrator</strong> to request access</li>
          <li>Or email <a href="mailto:ahsupport@truefocusnow.com">ahsupport@truefocusnow.com</a></li>
        </ul>`,
      },
      {
        id: 'B4',
        q: 'What are the password requirements?',
        a: `Your password must:
        <ul>
          <li>Be <strong>6 to 15 characters</strong> long</li>
          <li>Contain at least one <strong>uppercase</strong> letter (A–Z)</li>
          <li>Contain at least one <strong>lowercase</strong> letter (a–z)</li>
          <li>Contain at least one <strong>number</strong> (0–9)</li>
          <li>Contain at least one <strong>special character</strong> (!@#$%^&* etc.)</li>
          <li>Contain <strong>no spaces</strong></li>
        </ul>`,
      },
      {
        id: 'B5',
        q: 'What ZIP code format is required?',
        a: `US ZIP codes accepted: <span class="chip">90210</span> (5 digits) or <span class="chip">90210-1234</span> (5+4 digits).`,
      },
      {
        id: 'B6',
        q: 'Can I add appraisers to my organization after registering?',
        a: `Yes. Once your self-signup is successful, you become the <strong>Admin</strong>, and you can add new users with the <strong>Appraiser</strong> role — each with their own login.`,
      },
      {
        id: 'B7',
        q: 'Can I change my organization details after registration?',
        a: `Organization name and address can be updated by a Super Admin. Contact your Admin or email <a href="mailto:ahsupport@truefocusnow.com">ahsupport@truefocusnow.com</a>.`,
      },
      {
        id: 'B8',
        q: 'Does my organization need manual approval?',
        a: `No. Registration creates an <strong>auto-approved</strong> organization account. You can log in and start immediately after registering.`,
      },
    ],
  },
  {
    id: 'sec-c',
    icon: '🔐',
    title: 'Login, MFA & Password',
    items: [
      {
        id: 'C1',
        q: 'How do I log in?',
        a: `Visit <a href="https://appraisal.truefocusautomation.com/login">appraisal.truefocusautomation.com/login</a>, enter your <strong>Email Address</strong> and <strong>Password</strong>, then click "Sign In." You will then be prompted for a 6-digit MFA code sent to your email.`,
      },
      {
        id: 'C2',
        q: 'What is MFA (Multi-Factor Authentication)?',
        a: `MFA adds a <strong>second layer of security</strong>. After your password, a 6-digit one-time code is sent to your registered email. You must enter it to complete login.`,
      },
      {
        id: 'C3',
        q: 'I did not receive my MFA code. What do I do?',
        a: `<ul>
          <li>Check your <strong>spam/junk</strong> email folder</li>
          <li>Wait 1–2 minutes — delivery can be slightly delayed</li>
          <li>Click the <strong>"Resend"</strong> button on the MFA screen</li>
          <li>Verify your registered email is correct</li>
          <li>Contact <a href="mailto:ahsupport@truefocusnow.com">ahsupport@truefocusnow.com</a> if issue persists</li>
        </ul>`,
      },
      {
        id: 'C4',
        q: 'Do I need to enter all 6 MFA digits?',
        a: `Yes. All 6 digits are required. The system blocks submission until all 6 are entered. You can also <strong>paste the code directly</strong> from your email.`,
      },
      {
        id: 'C5',
        q: 'How do I reset my password?',
        a: `<ol>
          <li>Click <strong>"Forgot password?"</strong> on the login page</li>
          <li>Enter your registered email address</li>
          <li>Check your email for a reset link</li>
          <li>Click the link and enter your new password</li>
        </ol>
        <div class="note">⚠️ The reset link is time-sensitive. If expired, repeat the process.</div>`,
      },
      {
        id: 'C6',
        q: 'I requested a password reset but got no email.',
        a: `<ul>
          <li>Check spam/junk folder</li>
          <li>Confirm you used the correct registered email</li>
          <li>Wait a few minutes and retry</li>
          <li>Email <a href="mailto:ahsupport@truefocusnow.com">ahsupport@truefocusnow.com</a> if still unresolved</li>
        </ul>`,
      },
      {
        id: 'C7',
        q: 'My account is locked or inactive. What do I do?',
        a: `Contact your <strong>Organization Admin</strong> — they can activate or deactivate user accounts. If unavailable, email <a href="mailto:ahsupport@truefocusnow.com">ahsupport@truefocusnow.com</a>.`,
      },
      {
        id: 'C8',
        q: 'My session expired. What do I do?',
        a: `Simply <strong>log in again</strong>. For your security, your session ends automatically after a period of inactivity.`,
      },
    ],
  },
  {
    id: 'sec-d',
    icon: '👥',
    title: 'User Roles & Permissions',
    items: [
      {
        id: 'D1',
        q: 'What user roles are available?',
        a: `<ul>
          <li><span class="tag">Admin</span> — Full control of your organization: manages orders, users, credits, and settings. This is the role created automatically when you self sign up.</li>
          <li><span class="tag">Appraiser</span> — Processes and reviews appraisal orders. Your Admin creates this role; multiple Appraisers can be added.</li>
          <li><span class="tag">User</span> — Can place orders but does not have access to review or complete an order. Your Admin creates this role; multiple Users can be added.</li>
        </ul>`,
      },
      {
        id: 'D2',
        q: 'What can an Admin do?',
        a: `<ul>
          <li>Add and manage users in your organization</li>
          <li>Add credits and manage the credit wallet</li>
          <li>Place and manage appraisal orders</li>
          <li>Access reports for your organization</li>
        </ul>`,
      },
      {
        id: 'D3',
        q: 'What can a User do?',
        a: `Users can place orders, but they don't have access to the Review workflow or to completing an order. Your Admin creates User accounts and can add multiple of them.`,
      },
      {
        id: 'D4',
        q: 'What extra fields does an Appraiser profile have?',
        a: `<ul>
          <li>License Number</li>
          <li>License Expiry Date</li>
          <li>Appraiser Signature (file upload)</li>
          <li>Appraiser Certificate (file upload)</li>
        </ul>`,
      },
      {
        id: 'D5',
        q: 'Can a user have multiple roles?',
        a: `No. Each user is assigned <strong>one role</strong>. Contact your Admin to change a role.`,
      },
      {
        id: 'D6',
        q: 'Can I see data from other organizations?',
        a: `No. Your data is scoped to your organization only.`,
      },
    ],
  },
  {
    id: 'sec-h',
    icon: '💳',
    title: 'Credits & Wallet',
    items: [
      {
        id: 'H1',
        q: 'How does the credit system work?',
        a: `Credits are consumed from your organization's wallet each time an appraisal order is placed. You can purchase credits via our payment processor, or receive them from your administrator.`,
      },
      {
        id: 'H2',
        q: 'What types of credits are there?',
        a: `<ul>
          <li><span class="tag">Paid Credits</span> — Purchased via our payment processor</li>
          <li><span class="tag">Free Credits</span> — Granted at no charge (trial, promotions)</li>
          <li><span class="tag">Credit Line</span> — Added as a debt requiring repayment</li>
        </ul>`,
      },
      {
        id: 'H4',
        q: 'What is the Low Balance Alert?',
        a: `An optional notification displayed when your Available Balance falls at or below a configured threshold. This option must be enabled from <strong>Settings</strong>.`,
      },
      {
        id: 'H5',
        q: 'Can I see my credit balance in real time?',
        a: `Yes. Your credit balance updates automatically — if it doesn't, click the <strong>refresh</strong> button in the Credits section.`,
      },
      {
        id: 'H6',
        q: 'Where can I view my credit and transaction history?',
        a: `<ul>
          <li><strong>Payment History</strong> (<span class="chip">/payment-history</span>): Payment transactions with amounts, dates, and receipt links</li>
          <li><strong>Transaction History</strong> (<span class="chip">/transaction-history</span>): All credit movements in your account</li>
        </ul>`,
      },
    ],
  },
  {
    id: 'sec-i',
    icon: '💰',
    title: 'Payments & Billing',
    items: [
      {
        id: 'I1',
        q: 'How do I purchase credits?',
        a: `Go to the Payments page (<span class="chip">/payments</span>). Select a preset amount or enter a custom value, then click <strong>"Proceed to Pay."</strong> You'll be directed to our secure payment screen.`,
      },
      {
        id: 'I3',
        q: 'When will credits appear after payment?',
        a: `Credits are added <strong>instantly</strong> to your wallet upon successful payment.`,
      },
      {
        id: 'I4',
        q: 'How do I get a receipt for my payment?',
        a: `On the Payment History page, each transaction has a <strong>"Receipt"</strong> button that opens the receipt in a new tab.`,
      },
      {
        id: 'I5',
        q: 'Can I download my payment history?',
        a: `Yes. Click <strong>"Download History"</strong> on the Payment History page to export a PDF. File name: <span class="chip">Transaction_History_YYYY-MM-DD.pdf</span>`,
      },
      {
        id: 'I6',
        q: 'I was charged but credits were not added. What do I do?',
        a: `Contact <a href="mailto:ahsupport@truefocusnow.com">ahsupport@truefocusnow.com</a> immediately with your <strong>Payment ID</strong> (visible in Payment History), date, amount, and organization name.`,
      },
    ],
  },
  {
    id: 'sec-e',
    icon: '📋',
    title: 'Order Management',
    items: [
      {
        id: 'E1',
        q: 'How do I place a new appraisal order?',
        a: `From the Dashboard, click <strong>"Create Order"</strong> to open the order form. The following fields are mandatory:
        <ul>
          <li><strong>File Number</strong></li>
          <li><strong>Product Type</strong></li>
          <li><strong>Last Name</strong> (Current Owner)</li>
          <li><strong>Street Number</strong></li>
          <li><strong>Street Name</strong></li>
          <li><strong>City</strong></li>
          <li><strong>State</strong></li>
          <li><strong>Zip</strong></li>
          <li><strong>County</strong></li>
        </ul>
        All other fields (Loan Number, Lender/Client details, First/Middle Name, Street Prefix/Type/Suffix, Unit, APN) are optional. Fill in the required fields and submit — the order appears in the queue with status "Unassigned."`,
      },
      {
        id: 'E2',
        q: 'What do the order statuses mean?',
        a: `<ul>
          <li><span class="tag">Unassigned</span> — Received, not yet assigned</li>
          <li><span class="tag">Work In Progress</span> — Actively being worked on</li>
          <li><span class="tag">Review</span> — Ready for comparable review and report prep</li>
          <li><span class="tag">Completed</span> — Final report generated; order is locked</li>
          <li><span class="tag">Exception</span> — Issue requiring attention</li>
          <li><span class="tag">Cancelled</span> — Cancelled; no further actions</li>
        </ul>`,
      },
      {
        id: 'E3',
        q: 'Can I add multiple property owners to an order?',
        a: `Yes, up to <strong>3 owners</strong> per order (1 primary + 2 additional). Once 3 are added, the "Add Owners" button is disabled and shows "Maximum of 3 owners reached."`,
      },
      {
        id: 'E4',
        q: 'Can I upload multiple orders at once?',
        a: `Yes. Use the <strong>Bulk Upload</strong> option to upload an Excel or CSV file. Download the Bulk Upload file template from the <strong>Create Order</strong> screen.`,
      },
      {
        id: 'E5',
        q: 'Can I edit an order after submitting?',
        a: `Yes, as long as the status is <strong>not Completed or Cancelled</strong>. Once an order reaches either status, editing is permanently disabled.`,
      },
      {
        id: 'E6',
        q: 'Can I change the product type after placing an order?',
        a: `<strong>No.</strong> Product Type is locked in edit mode. If the wrong type was selected, place a fresh order with the correct product type.`,
      },
      {
        id: 'E7',
        q: 'How do I search for a specific order?',
        a: `Use the search bar to find orders by <strong>File Number</strong>. Filter dropdowns allow filtering by Status or Product Type.`,
      },
      {
        id: 'E8',
        q: 'I see a low credit balance warning on the order form. What does it mean?',
        a: `Your available balance is lower than the set Low Balance Alert threshold. Click the <strong>"Add Credits here"</strong> link in the warning to visit the Payments page and top up. You can also enable or disable this pop-up from <strong>Settings</strong>.`,
      },
      {
        id: 'E9',
        q: 'What are valid Street Prefix values?',
        a: `Directional values only (max 2 characters): <span class="chip">N</span> <span class="chip">S</span> <span class="chip">E</span> <span class="chip">W</span> <span class="chip">NE</span> <span class="chip">NW</span> <span class="chip">SE</span> <span class="chip">SW</span>`,
      },
      {
        id: 'E10',
        q: 'How do I download a completed order report?',
        a: `Find the completed order in the queue and click the <strong>"Download"</strong> button. Standard products download as PDF; Desktop Appraisal-Basic downloads as a ZIP file.`,
      },
      {
        id: 'E11',
        q: 'What does "Same as Current Owner" mean on the form?',
        a: `If the borrower/applicant is the same person as the property owner, check this box. It removes the requirement to separately enter a Borrower Name.`,
      },
      {
        id: 'E12',
        q: 'How many orders are shown per page?',
        a: `The order queue displays <strong>10 orders per page</strong> with pagination controls. Orders can be sorted by Order Date ascending or descending.`,
      },
    ],
  },
  {
    id: 'sec-f',
    icon: '🔍',
    title: 'Comparable Review',
    items: [
      {
        id: 'F1',
        q: 'What is the Comparable Review workflow?',
        a: `When an order reaches "Review" status, authorized users click the <strong>"Review"</strong> button to access the Comparable Review page — where you view, add, edit, delete, reorder, and select comparable property sales for the appraisal report.`,
      },
      {
        id: 'F2',
        q: 'Who can access the Comparable Review page?',
        a: `All roles <strong>except User</strong> (view-only access). Also, <strong>Desktop Appraisal-Basic</strong> product orders have no Review workflow — the button is hidden for those orders.`,
      },
      {
        id: 'F3',
        q: 'Can I reorder the comparables?',
        a: `Yes. Comparables can be reordered to change their sequence in the report.`,
      },
      {
        id: 'F4',
        q: 'Can I delete a comparable?',
        a: `Yes. Comparables can be deleted using the delete action.`,
      },
      {
        id: 'F5',
        q: 'Can I edit a comparable after adding it?',
        a: `Yes. Comparable data can be updated at any time while the order is in Review status, before the final report is generated.`,
      },
    ],
  },
  {
    id: 'sec-g',
    icon: '📄',
    title: 'Report Generation',
    items: [
      {
        id: 'G1',
        q: 'Can I preview a draft report before finalizing?',
        a: `Yes. Use the <strong>"Download Draft"</strong> button on the Preview Page to download a draft PDF for review before committing to the final report.`,
      },
      {
        id: 'G2',
        q: 'Can I edit an order after generating the final report?',
        a: `<strong>No.</strong> Once the final report is generated, the order is permanently locked (status: Completed). This preserves the integrity of the appraisal document.
        <div class="note">⚠️ A confirmation dialog appears before finalizing: "You won't be able to edit this order after generating the final PDF. Are you sure you want to proceed?"</div>`,
      },
      {
        id: 'G3',
        q: 'What file types can I upload as additional documents?',
        a: `Only <strong>PDF files</strong> are accepted.`,
      },
      {
        id: 'G4',
        q: 'What are TPAR and TMAR?',
        a: `<strong>TPAR</strong> (Total Price Adjustment Range) and <strong>TMAR</strong> (Total Market Adjustment Range) are analytical values in the Identification & Market Area section that quantify market adjustments in the appraisal.`,
      },
      {
        id: 'G5',
        q: 'Where can I find the final report after generation?',
        a: `The report opens in a new browser tab immediately after generation. It is also available via the <strong>"Download"</strong> button on any Completed order in the Dashboard queue.`,
      },
    ],
  },
  {
    id: 'sec-j',
    icon: '👤',
    title: 'Users & Team Management',
    items: [
      {
        id: 'J1',
        q: 'How do I add a new user to my organization?',
        a: `As an Admin, go to <strong>Users → User Administration</strong> (<span class="chip">/userlist</span>). Use "Add User" to create a new account with their name, email, and role.`,
      },
      {
        id: 'J2',
        q: 'How do I deactivate a user without deleting them?',
        a: `Use the <strong>Activate/Deactivate toggle</strong> on the User List page. Deactivated users cannot log in, but their data and history are preserved.`,
      },
      {
        id: 'J3',
        q: "Can I edit a user's role?",
        a: `Yes. Admins can edit user details including role via User Administration → Edit.`,
      },
      {
        id: 'J4',
        q: 'Can a user belong to multiple organizations?',
        a: `No. Each user account is associated with <strong>one organization</strong>.`,
      },
      {
        id: 'J5',
        q: "How do I upload an appraiser's signature or certificate?",
        a: `On the user edit form, use the file upload fields for <strong>Appraiser Signature</strong> and <strong>Appraiser Certificate</strong>. Files are securely uploaded to cloud storage.`,
      },
      {
        id: 'J6',
        q: 'What if my Appraiser Signature or Certificate needs to be updated?',
        a: `Your Admin can replace it at any time. Go to <strong>Users → User Administration → Edit</strong> and re-upload the file in the <strong>Appraiser Signature</strong> or <strong>Appraiser Certificate</strong> field — the new file replaces the old one.`,
      },
    ],
  },
  {
    id: 'sec-m',
    icon: '📊',
    title: 'Reports & Analytics',
    items: [
      {
        id: 'M1',
        q: 'What reports are available?',
        a: `<ul>
          <li><strong>Operational Report</strong> — Daily or custom date range appraisal activity report</li>
          <li><strong>Finance Report</strong> — Credit Report or Payment Report for a custom date range</li>
        </ul>`,
      },
      {
        id: 'M2',
        q: "How do I download today's activity report?",
        a: `On the Dashboard, find the <strong>Report Card</strong> section and click "Daily Report." Today's report is generated and displayed immediately.`,
      },
      {
        id: 'M3',
        q: 'What is the Finance Report?',
        a: `The Finance Report shows financial data for your organization. Choose between:
        <ul>
          <li><strong>Credit Report</strong> — credit usage and movements</li>
          <li><strong>Payment Report</strong> — Payment transactions</li>
        </ul>
        Select the type and a date range (both Start and End dates required).`,
      },
      {
        id: 'M4',
        q: 'Who can access reports?',
        a: `<ul>
          <li>Admins — Report Card and Finance Report Card</li>
          <li>Appraisers / Users — <strong>No access</strong> to reports</li>
        </ul>`,
      },
      {
        id: 'M5',
        q: 'Can I export reports?',
        a: `Yes:
        <ul>
          <li>Payment History → Download as PDF</li>
          <li>Operational & Finance Reports → Open in fullscreen viewer with download options</li>
        </ul>`,
      },
    ],
  },
  {
    id: 'sec-n',
    icon: '🔧',
    title: 'Troubleshooting',
    items: [
      {
        id: 'N1',
        q: 'The page is not loading. What should I do?',
        a: `<ul>
          <li>Refresh the browser (<span class="chip">F5</span> or <span class="chip">Ctrl+R</span>)</li>
          <li>Clear browser cache and cookies</li>
          <li>Try a different browser (Chrome, Edge, Firefox recommended)</li>
          <li>Check your internet connection</li>
          <li>Contact <a href="mailto:ahsupport@truefocusnow.com">ahsupport@truefocusnow.com</a> if issue persists</li>
        </ul>`,
      },
      {
        id: 'N2',
        q: 'I cannot see the Review button on an order. Why?',
        a: `The Review button is hidden when:
        <ul>
          <li>Your role is <strong>User</strong> (no Review access)</li>
          <li>The order product type is <strong>Desktop Appraisal-Basic</strong> (no Review workflow)</li>
          <li>The order status is <strong>not yet "Review"</strong></li>
        </ul>`,
      },
      {
        id: 'N3',
        q: 'I cannot change the status of an order.',
        a: `Status changes are blocked when:
        <ul>
          <li>Order is already <strong>Completed or Cancelled</strong> (permanently locked)</li>
          <li>Your role is <strong>User</strong> (status changes not permitted)</li>
        </ul>`,
      },
      {
        id: 'N4',
        q: 'I uploaded a file and got "File type not supported."',
        a: `Only <strong>PDF files</strong> (.pdf) are accepted for additional document uploads. Convert your file to PDF and try again.`,
      },
      {
        id: 'N5',
        q: 'The State dropdown is empty during registration.',
        a: `The state list loads from the platform's initialization data. If empty, it may be a slow network issue. <strong>Refresh the page</strong> and try again. All 50 US states should be available.`,
      },
      {
        id: 'N6',
        q: 'The Download button is not appearing for my order.',
        a: `The Download button only appears for orders with <strong>Completed</strong> status. Ensure the final report has been generated.`,
      },
      {
        id: 'N7',
        q: 'My bulk upload is missing some orders.',
        a: `Check your file for: missing required fields, invalid data formats, or duplicate file numbers. Contact <a href="mailto:ahsupport@truefocusnow.com">ahsupport@truefocusnow.com</a> with the file for review.`,
      },
    ],
  },
  {
    id: 'sec-o',
    icon: '🔒',
    title: 'Security & Data',
    items: [
      {
        id: 'O1',
        q: 'How is my data secured?',
        a: `<ul>
          <li><strong>Encrypted connections</strong> for all data sent to and from the platform</li>
          <li><strong>Secure sessions</strong> that expire automatically for your protection</li>
          <li><strong>Secure, PCI-compliant payment processing</strong> via our payment processor</li>
          <li><strong>Secure cloud storage</strong> for document uploads</li>
          <li><strong>Role-based access</strong> — you only see data relevant to your role</li>
          <li><strong>MFA</strong> required for all logins</li>
        </ul>`,
      },
      {
        id: 'O2',
        q: 'Are my payment card details stored on Appraisal Hunter?',
        a: `<strong>No.</strong> All card data is handled exclusively by our card payment processor. Appraisal Hunter never stores, processes, or transmits your card information.`,
      },
      {
        id: 'O3',
        q: "Who can see my organization's data?",
        a: `Only users within your organization (by role) and Super Admins of the platform. No other organization can access your data.`,
      },
      {
        id: 'O4',
        q: 'Can I request deletion of my account and data?',
        a: `Email <a href="mailto:ahsupport@truefocusnow.com">ahsupport@truefocusnow.com</a> to submit a data deletion request. Handled manually to ensure proper data management.`,
      },
    ],
  },
]

export default faqData